import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useToggle } from 'usehooks-ts';

import { MenuItem, OverflowMenu, Tag, spacing } from '@pletly/ui';
import ChevronIcon from '../../chevron-down.svg';

export type RoleMenuOption<OptionValueType> = {
	title: string;
	value: OptionValueType;
};

type RoleMenuProps<OptionValueType> = {
	options: RoleMenuOption<OptionValueType>[];
	currentOption: RoleMenuOption<OptionValueType>;
	onRoleChange: (selectedOptionValue: OptionValueType) => unknown;
	disabled?: boolean;
};
export const RoleMenu = <OptionValueType,>({
	options,
	currentOption,
	onRoleChange,
	disabled = false,
}: RoleMenuProps<OptionValueType>) => {
	const [isMenuOpen, toggleOpen] = useToggle();

	const handleRoleOptionPress = useCallback(
		(selectedOptionValue: OptionValueType) => () => {
			toggleOpen();
			if (currentOption.value === selectedOptionValue) {
				return;
			}

			return onRoleChange(selectedOptionValue);
		},
		[toggleOpen, onRoleChange, currentOption.value],
	);

	const renderAnchor = useCallback(
		() => (
			<Pressable onPress={toggleOpen} style={styles.anchorBtn} disabled={disabled}>
				<Tag text={currentOption.title} size="small" numberOfLines={1} style={styles.tag} />
				{!disabled && <ChevronIcon />}
			</Pressable>
		),
		[toggleOpen, currentOption.title, disabled],
	);

	return (
		<View style={styles.root}>
			<OverflowMenu
				visible={isMenuOpen}
				onBackdropPress={toggleOpen}
				anchor={renderAnchor}
				style={styles.menu}
			>
				{options.map((option) => (
					<MenuItem
						key={option.title}
						title={option.title}
						onPress={handleRoleOptionPress(option.value)}
					/>
				))}
			</OverflowMenu>
		</View>
	);
};

const styles = StyleSheet.create({
	root: { flex: 1 },
	menu: {
		marginTop: spacing(),
	},
	anchorBtn: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing(0.5),
	},
	tag: { maxWidth: '85%' },
});
